import type { Feed, ScheduleSlot } from "../../api/types.js";

/**
 * Which feeds use a global object — worked out from the feeds, because that is the
 * direction the reference runs.
 *
 * A playlist does not know what plays it and must not: it is a way of choosing from
 * the library, and a feed naming it is the feed's fact, not the playlist's. So this
 * reads every feed's three references — its default playlist, its fallback playlist,
 * and every playlist reachable through the schedule it is assigned — and answers the
 * question the operator actually asks, which is "if I change this, what changes?"
 *
 * One playlist driving three feeds is what this function is for. It is not a warning,
 * it is not a conflict, and nothing downstream of it may present it as one.
 */

export type PlaylistRole = "default" | "fallback" | "scheduled";

export interface FeedUse {
  feedId: string;
  feedName: string;
  /** A switched-off feed still uses the playlist; it is simply not on the air. */
  feedEnabled: boolean;
  /** Every way this feed reaches the object, in the order they were checked. */
  roles: PlaylistRole[];
}

const ROLE_LABELS: Record<PlaylistRole, string> = {
  default: "default",
  fallback: "fallback",
  scheduled: "scheduled",
};

export function describeRoles(roles: readonly PlaylistRole[]): string {
  return roles.map((role) => ROLE_LABELS[role]).join(" · ");
}

/**
 * Every feed that reaches this playlist, and how. `slotsBySchedule` is what has been
 * read of each schedule's slots; a schedule whose slots have not been read yet simply
 * contributes nothing, so the answer grows rather than being wrong in the meantime.
 */
export function feedsUsingPlaylist(
  playlistId: string,
  feeds: readonly Feed[],
  slotsBySchedule: Readonly<Record<string, readonly ScheduleSlot[]>>,
): FeedUse[] {
  if (playlistId === "") return [];
  const uses: FeedUse[] = [];
  for (const feed of feeds) {
    const roles: PlaylistRole[] = [];
    if (feed.defaultPlaylistId === playlistId) roles.push("default");
    if (feed.fallbackPlaylistId === playlistId) roles.push("fallback");
    if (feed.scheduleId !== null) {
      const slots = slotsBySchedule[feed.scheduleId] ?? [];
      if (slots.some((slot) => slot.playlistId === playlistId)) {
        roles.push("scheduled");
      }
    }
    if (roles.length > 0) {
      uses.push({
        feedId: feed.id,
        feedName: feed.name,
        feedEnabled: feed.enabled,
        roles,
      });
    }
  }
  return uses;
}

/** The same answer for every playlist at once, for a screen that lists them. */
export function playlistUsageIndex(
  playlistIds: readonly string[],
  feeds: readonly Feed[],
  slotsBySchedule: Readonly<Record<string, readonly ScheduleSlot[]>>,
): Record<string, FeedUse[]> {
  const index: Record<string, FeedUse[]> = {};
  for (const playlistId of playlistIds) {
    index[playlistId] = feedsUsingPlaylist(playlistId, feeds, slotsBySchedule);
  }
  return index;
}

/** Every feed assigned this schedule. A schedule is assigned, never owned. */
export function feedsUsingSchedule(
  scheduleId: string,
  feeds: readonly Feed[],
): FeedUse[] {
  if (scheduleId === "") return [];
  return feeds
    .filter((feed) => feed.scheduleId === scheduleId)
    .map((feed) => ({
      feedId: feed.id,
      feedName: feed.name,
      feedEnabled: feed.enabled,
      roles: [] as PlaylistRole[],
    }));
}

/**
 * How a set of users is said in one line. "No feed" is a plain statement of fact and
 * not a problem — a playlist nothing plays yet is an ordinary thing to have made.
 */
export function summariseUses(uses: readonly FeedUse[]): string {
  if (uses.length === 0) return "No feed";
  if (uses.length === 1) return uses[0]?.feedName ?? "";
  return `${String(uses.length)} feeds`;
}
